import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { trackEventBudget, TRACKABLE_EVENTS } from 'helpers/trackerHelpers';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import Flex from 'ui/Flex/Flex';
import ExportButtonMultipleGrid from 'components/ExportButton/ExportButtonMultipleGrid';
import {
    getExcelFilename,
    MULTI_YEAR_COMPARISON_PREFIX,
} from 'routes/BudgetModule/contexts/helpers';
import MultiYearsComparisonGrid from './components/MultiYearsComparisonGrid';
import { getIndicatorsForExercice } from '../../businessHelper';
import { useGetMultiYearsComparison } from './hooks/useGetMultiYearsComparison';
import { useDispatch } from 'store/hooks';
import { MultiYearsComparisonRow } from 'routes/BudgetModule/components/BudgetOverview/BudgetSynthesisAndRatios/MultiYearsComparison/types';

type Props = {
    budgetName: string | null | undefined;
    years: number[];
    referenceYear: number;
};

const StyTitle = styled(Box, {
    name: 'StyTitle',
})(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    alignItems: 'center',
    marginTop: '2rem',
    [theme.breakpoints.down('lg')]: {
        width: '100%',
    },
}));

const StyRefreshButton = styled(Button, {
    name: 'StyRefreshButton',
})(({ theme }) => ({
    height: '26px',
    minHeight: '26px',
    [theme.breakpoints.down(780)]: {
        height: '48px',
        minHeight: '48px',
    },
}));

const StyTableContainer = styled(Box, {
    name: 'StyTableContainer',
})({
    width: '100%',
    '& .table-chart': {
        height: 'auto',
    },
});

export default function MultiYearsComparison(props: Props): React.ReactElement {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const dispatch = useDispatch();

    const [refreshing, setRefreshing] = React.useState<boolean>(false);
    const { budgetName, years, referenceYear } = props;
    const { data: results, isLoading } = useGetMultiYearsComparison(
        referenceYear,
        budgetName,
    );

    const phase = results?.data?.find(
        (item) => item.year === referenceYear,
    )?.amountType;

    const formattedData: MultiYearsComparisonRow[] = [
        { name: t('budgetOverview.cafBrut') },
        { name: t('budgetOverview.cafNet') },
        {
            name: t('budgetOverview.excedentBrut'),
        },
        {
            name: t('budgetOverview.cafBrutRatio'),
        },
        {
            name: t('budgetOverview.cafNetRatio'),
        },
        {
            name: t('budgetOverview.excedentBrutRatio'),
        },
    ];

    years.forEach((year) => {
        const {
            cafBrut,
            cafNet,
            excedentBrut,
            cafBrutRatio,
            cafNetRatio,
            excedentBrutRatio,
        } = getIndicatorsForExercice(results?.data ?? [], year);

        formattedData[0][year] = { value: cafBrut, type: 'currency' };
        formattedData[1][year] = { value: cafNet, type: 'currency' };
        formattedData[2][year] = { value: excedentBrut, type: 'currency' };
        formattedData[3][year] = { value: cafBrutRatio, type: 'percentage' };
        formattedData[4][year] = { value: cafNetRatio, type: 'percentage' };
        formattedData[5][year] = {
            value: excedentBrutRatio,
            type: 'percentage',
        };
    });

    const refreshData = () => {
        setRefreshing(true);
        queryClient
            .invalidateQueries({
                queryKey: ['multi_year_comparison', referenceYear, budgetName],
            })
            .finally(() => {
                setRefreshing(false);
                dispatch.vision.notifyUser({
                    message: t('budgetOverview.dataRefreshed'),
                    snackType: 'success',
                });
            });
        trackEventBudget(
            TRACKABLE_EVENTS.budget.budgetOverview.totals.refreshColumns,
        );
    };

    return (
        <>
            <StyTitle>
                <Typography variant="h5">
                    {t('budgetOverview.multiYearsComparison')}
                </Typography>
                <Flex flexDirection="row">
                    <ExportButtonMultipleGrid
                        fileName={getExcelFilename(
                            MULTI_YEAR_COMPARISON_PREFIX,
                        )}
                        event={
                            TRACKABLE_EVENTS.budget.budgetOverview.totals
                                .exportRatio
                        }
                    />
                    <StyRefreshButton variant="contained" onClick={refreshData}>
                        {t('budgetOverview.refreshData')}
                    </StyRefreshButton>
                </Flex>
            </StyTitle>
            <StyTableContainer>
                <MultiYearsComparisonGrid
                    phase={phase}
                    year={referenceYear}
                    data={formattedData}
                    isLoading={isLoading || refreshing}
                />
            </StyTableContainer>
        </>
    );
}
